import React, { useState } from 'react';
import { BaseTool, IToolManifest, IToolComponentProps, IToolContext } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { CreateShapeCommand } from '../../core/commands/CreateShapeCommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import { StickyNote } from 'lucide-react';
import { cn } from '../../lib/utils';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';
import i18n from '../../i18n/config';

declare global {
  interface Window {
    __openStickyNote?: () => void;
  }
}

export const STICKY_NOTE_COLORS = [
  { id: 'yellow', color: '#FDF097', border: '#F8E164', label: 'Yellow' },
  { id: 'pink', color: '#EDACD2', border: '#E479B3', label: 'Pink' },
  { id: 'blue', color: '#89E5F6', border: '#65D0EA', label: 'Blue' },
  { id: 'green', color: '#AEF4E4', border: '#86E7D4', label: 'Green' },
  { id: 'purple', color: '#C1B6F8', border: '#A38CF3', label: 'Purple' },
  { id: 'orange', color: '#F8D8B0', border: '#F3BD7F', label: 'Orange' },
  { id: 'gray', color: '#E3E8EF', border: '#97A3B6', label: 'Gray' },
  { id: 'white', color: '#FFFFFF', border: '#CDD5E0', label: 'White' },
];

const StickyNoteToolComponent: React.FC<IToolComponentProps> = ({ onExecute }) => {
  const [open, setOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState('yellow');

  React.useEffect(() => {
    window.__openStickyNote = () => setOpen(true);
    return () => {
      delete window.__openStickyNote;
    };
  }, []);
  
  const handleColorSelect = (colorId: string) => {
    setSelectedColor(colorId);
    onExecute({ color: colorId });
    setOpen(false);
  };

  const currentColor = STICKY_NOTE_COLORS.find(c => c.id === selectedColor) || STICKY_NOTE_COLORS[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="w-0 h-0 opacity-0 pointer-events-none fixed bottom-6 left-[calc(50%-100px)]"
        >
          <div
            className="w-6 h-6 rounded"
            style={{
              backgroundColor: currentColor.color,
              border: `2px solid ${currentColor.border}`
            }}
          />
          <StickyNote className="h-4 w-4 absolute text-[#475569]" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-3"
        align="center"
        side="top"
        sideOffset={80}
      >
        <div className="grid grid-cols-4 gap-3">
          {STICKY_NOTE_COLORS.map((noteColor) => (
            <button
              key={noteColor.id}
              data-testid={`tid.tools.stickyNote.color.${noteColor.id}`}
              onClick={() => handleColorSelect(noteColor.id)}
              className={cn(
                "w-10 h-10 rounded-lg border-1 transition-all",
                "hover:scale-110",
                selectedColor === noteColor.id 
                  ? "shadow-sm" 
                  : undefined
              )}
              style={{ 
                backgroundColor: noteColor.color,
                borderColor: selectedColor === noteColor.id ? '#3478DD' : noteColor.border
              }}
              aria-label={noteColor.label}
            >
              {selectedColor === noteColor.id && (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-[#364153] rounded-full" />
                </div>
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export class StickyNoteTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'sticky-note',
    name: 'Sticky Note',
    icon: 'sticky-note',
    category: 'create',
    description: 'Add sticky notes',
    version: '1.0.0',
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  // Placement state (mirrors ShapePickerTool for drag-to-place UX)
  private placing = false;
  private startPoint: { x: number; y: number } | null = null;
  private chosen = STICKY_NOTE_COLORS[0];
  private defaultSize = { width: 150, height: 150 };
  private documentPointerListenersAttached = false;

  onActivate(context: IToolContext): void {
    super.onActivate(context);
    if (this.placing && this.context?.stage) {
      this.setStageCursor('crosshair');
    }
    if (this.context?.stage) {
      const stage = this.context.stage;
      stage.off('.place-sticky');
      stage.on('mousedown.place-sticky', this.handleMouseDown);
      stage.on('mousemove.place-sticky', this.handleMouseMove);
      stage.on('mouseup.place-sticky', this.handleMouseUp);
    }
  }

  onDeactivate(): void {
    this.detachDocumentPointerListeners();
    if (this.context?.stage) {
      this.context.stage.off('.place-sticky');
      this.setStageCursor('default');
    }
    super.onDeactivate();
  }

  private attachDocumentPointerListeners(): void {
    if (this.documentPointerListenersAttached) return;
    window.addEventListener('mouseup', this.handleDocumentMouseUp, true);
    window.addEventListener('touchend', this.handleDocumentTouchEnd, true);
    this.documentPointerListenersAttached = true;
  }

  private detachDocumentPointerListeners(): void {
    if (!this.documentPointerListenersAttached) return;
    window.removeEventListener('mouseup', this.handleDocumentMouseUp, true);
    window.removeEventListener('touchend', this.handleDocumentTouchEnd, true);
    this.documentPointerListenersAttached = false;
  }

  private syncStagePointer(event: MouseEvent | TouchEvent): void {
    try {
      this.context?.stage?.setPointersPositions(event);
    } catch {
      // The stage may be mid-unmount while route-level cleanup is running.
    }
  }

  private handleDocumentMouseUp = (event: MouseEvent) => {
    if (!this.placing) return;
    this.syncStagePointer(event);
    this.handleMouseUp();
  };

  private handleDocumentTouchEnd = (event: TouchEvent) => {
    if (!this.placing) return;
    this.syncStagePointer(event);
    this.handleMouseUp();
  };

  private handleMouseDown = () => {
    if (!this.placing || !this.context?.stage) return;
    const stage = this.context.stage;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX();
    const origin = stage.position();
    const cx = (pos.x - origin.x) / scale;
    const cy = (pos.y - origin.y) / scale;
    this.startPoint = { x: cx, y: cy };
    this.attachDocumentPointerListeners();
    this.context.eventBus.emit('place:preview:start', {
      x: cx,
      y: cy,
      width: 1,
      height: 1,
      category: 'sticky-note',
      shapeId: 'sticky-note',
      fillColor: this.chosen.color,
      strokeColor: this.chosen.border
    });
  };

  private handleMouseMove = () => {
    if (!this.placing || !this.context?.stage) return;
    const stage = this.context.stage;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX();
    const origin = stage.position();
    const cx = (pos.x - origin.x) / scale;
    const cy = (pos.y - origin.y) / scale;
    if (this.startPoint) {
      const x = Math.min(this.startPoint.x, cx);
      const y = Math.min(this.startPoint.y, cy);
      const width = Math.abs(cx - this.startPoint.x);
      const height = Math.abs(cy - this.startPoint.y);
      this.context.eventBus.emit('place:preview:update', { x, y, width, height });
    } else {
      this.context.eventBus.emit('place:preview:update', {
        x: cx,
        y: cy,
        width: this.defaultSize.width,
        height: this.defaultSize.height
      });
    }
  };

  private handleMouseUp = () => {
    if (!this.placing || !this.context?.stage) return;
    if (!this.context.canWrite) {
      this.endPlacement();
      return;
    }
    const stage = this.context.stage;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX();
    const origin = stage.position();
    const cx = (pos.x - origin.x) / scale;
    const cy = (pos.y - origin.y) / scale;

    let x: number;
    let y: number;
    let width: number | undefined;
    let height: number | undefined;
    if (this.startPoint) {
      x = Math.min(this.startPoint.x, cx);
      y = Math.min(this.startPoint.y, cy);
      const w = Math.abs(cx - this.startPoint.x);
      const h = Math.abs(cy - this.startPoint.y);
      if (w < 3 && h < 3) {
        x = cx; y = cy; width = this.defaultSize.width; height = this.defaultSize.height;
      } else { width = w; height = h; }
    } else {
      x = cx; y = cy; width = this.defaultSize.width; height = this.defaultSize.height;
    }

    const cmd = new CreateShapeCommand({
      type: 'sticky-note',
      x,
      y,
      width,
      height,
      fillColor: this.chosen.color,
      strokeColor: this.chosen.border,
      text: i18n.t('genogram:shapes.stickyNoteDefault', { defaultValue: 'Note' }) as string,
      category: 'sticky-note',
      ydoc: this.context?.doc
    });
    this.context.eventBus.emit('command:execute', cmd);
    
    // Track sticky note creation (aggregated + first-time)
    SessionMetrics.increment('sticky_notes_created');
    SessionMetrics.firstTime('sticky_note_created');
    this.endPlacement();
  };

  execute(params?: { color: string }): ICommand | null {
    if (!this.context?.canWrite) {
      this.endPlacement();
      return null;
    }
    this.chosen = STICKY_NOTE_COLORS.find(c => c.id === params?.color) || STICKY_NOTE_COLORS[0];
    // Enter placement mode like shapes tool
    this.placing = true;
    this.setStageCursor('crosshair');
    // Seed preview at pointer if available
    if (this.context?.stage?.getPointerPosition()) {
      const pos = this.context.stage.getPointerPosition()!;
      const scale = this.context.stage.scaleX();
      const origin = this.context.stage.position();
      const cx = (pos.x - origin.x) / scale;
      const cy = (pos.y - origin.y) / scale;
      this.context.eventBus.emit('place:preview:start', {
        x: cx,
        y: cy,
        width: this.defaultSize.width,
        height: this.defaultSize.height,
        category: 'sticky-note',
        shapeId: 'sticky-note',
        fillColor: this.chosen.color,
        strokeColor: this.chosen.border
      });
    }
    return null;
  }

  private endPlacement(): void {
    this.detachDocumentPointerListeners();
    try {
      this.context?.eventBus.emit('place:preview:end');
    } catch {
      // Preview teardown is best-effort while the editor shell is unmounting.
    }
    this.placing = false;
    this.startPoint = null;
    this.setStageCursor('default');
  }

  private setStageCursor(cursor: string): void {
    try {
      const container = this.context?.stage?.container();
      if (container) container.style.cursor = cursor;
    } catch {
      // The Konva container may be unavailable during route teardown.
    }
  }

  getToolbarComponent() {
    return StickyNoteToolComponent;
  }
}
